import { z } from 'zod';

import { COMMON_DIMENSION_IDS, COMMON_UNIT_IDS } from './packs/common-unit-ids.js';
import {
  DimensionIdSchema,
  RequiredLocalizedNameSchema,
  UnitIdSchema,
  type DimensionedValue,
  type LocalizedString,
} from './predicate.js';
import {
  affineMap,
  convert as convertQuantity,
  decimalToString,
  fromCanonical,
  parseDecimal,
  toCanonical,
  type AffineMap,
  type Decimal,
} from './quantity.js';

/**
 * Units of measurement (08-units.md, ADR-0007). Measurement is modeled as a
 * **canonical base value per dimension** with **units/dimensions as
 * opaque-id projection definitions** — exact parity with how calendars
 * (ADR-0006) project onto the canonical day axis:
 *
 *   Quantity (value in a unit)  →  canonical base value  →  Display (a unit)
 *
 * A `number`-kind fact carries the canonical base value of its dimension
 * plus the authoring/display `unit_id` as provenance (see `DimensionedValue`
 * in predicate.ts). A unit is a pure **affine bijection** onto its
 * dimension's base value (`canonical = quantity × to_base + offset`); all of
 * that arithmetic — and the exact arbitrary-precision decimal canonical
 * value — lives in the `quantity.ts` chokepoint.
 *
 * Identity is the opaque, minted id (`dim_…` / `unit_…`, ADR-0007 §2); naming
 * is the mandatory localized `name`; `symbol` is the short display token
 * (`km`, `gp`). The id is never user-facing and never authored by hand or by
 * the LLM (server/client mints, ADR-0004 §1/§7).
 *
 * Cross-dimension math (length × length → area) is out of scope; we ship
 * same-dimension conversion only. **Currency** is a dimension whose
 * denominations are fixed-factor unit projections onto a canonical minor
 * unit (copper); intra-system conversion is a constant bijection like any
 * unit, while **cross-system exchange** (e.g. gp ↔ a foreign currency) is
 * time-varying and stays fact-modeled (08-units §7 exchange-rate facts),
 * never a `to_base` constant.
 */

/**
 * A measurable dimension (length, mass, currency, …). `base_unit` names the
 * unit the canonical value is denominated in (`to_base: "1"`, `offset: "0"`);
 * it is optional/informational — the canonical base is whichever unit has the
 * identity affine map — and references a `unit_…` id.
 */
export const DimensionDefinitionSchema = z
  .object({
    id: DimensionIdSchema,
    name: RequiredLocalizedNameSchema,
    base_unit: UnitIdSchema.optional(),
  })
  .strict();
export type DimensionDefinition = z.infer<typeof DimensionDefinitionSchema>;

/**
 * A single unit within a dimension. The affine map onto the dimension's
 * canonical base value is `canonical = value × to_base + offset`; `to_base`
 * and `offset` are exact **factor strings** parsed by `quantity.ts` — a
 * decimal (`"0.0254"`) or a fraction (`"5/9"`, so a non-terminating slope
 * like °F stays exact). `offset` defaults to `"0"` (ratio scales). `symbol`
 * is the short display token; `system` groups units for the picker UI
 * ("metric" / "imperial" / "currency").
 */
export const UnitDefinitionSchema = z
  .object({
    id: UnitIdSchema,
    name: RequiredLocalizedNameSchema,
    symbol: RequiredLocalizedNameSchema,
    dimension: DimensionIdSchema,
    to_base: z.string().min(1),
    offset: z.string().min(1).optional(),
    system: z.string().min(1).optional(),
  })
  .strict()
  .superRefine((u, ctx) => {
    // Reject a structurally-invalid affine map at the schema boundary (a
    // zero / unparseable scale is not invertible). Routed through the same
    // `quantity.ts` parser the conversion engine uses.
    try {
      affineMap(u.to_base, u.offset);
    } catch (e) {
      ctx.addIssue({
        code: 'custom',
        message: `unit "${u.id}" has an invalid affine map: ${(e as Error).message}`,
      });
    }
  });
export type UnitDefinition = z.infer<typeof UnitDefinitionSchema>;

/** The affine bijection a unit projects onto its dimension's canonical base. */
export function unitAffineMap(unit: UnitDefinition): AffineMap {
  return affineMap(unit.to_base, unit.offset);
}

/** Are two units inter-convertible (same dimension)? */
export function canConvert(from: UnitDefinition, to: UnitDefinition): boolean {
  return from.dimension === to.dimension;
}

/**
 * Convert a {@link Decimal} value between two units of the same dimension,
 * routing through the canonical base value (exact for terminating units).
 * Returns `null` when the dimensions differ (cross-dimension conversion is
 * undefined).
 */
export function convert(value: Decimal, from: UnitDefinition, to: UnitDefinition): Decimal | null {
  if (!canConvert(from, to)) return null;
  return convertQuantity(value, unitAffineMap(from), unitAffineMap(to));
}

/**
 * The canonical base value (as a decimal string) for `quantity` expressed in
 * `unit` — the exact thing stored on a dimensioned fact. `quantity` may be a
 * decimal string, a {@link Decimal}, or a JS number (number is for the
 * authoring boundary only).
 */
export function toCanonicalValue(
  quantity: Decimal | string | number,
  unit: UnitDefinition,
): string {
  const q =
    typeof quantity === 'string'
      ? parseDecimal(quantity)
      : typeof quantity === 'number'
        ? parseDecimal(quantity.toString())
        : quantity;
  return decimalToString(toCanonical(q, unitAffineMap(unit)));
}

/**
 * The display quantity (a {@link Decimal}) for a stored canonical base value
 * rendered back into `unit`. Inverse of {@link toCanonicalValue}.
 */
export function fromCanonicalValue(canonicalValue: string, unit: UnitDefinition): Decimal {
  return fromCanonical(parseDecimal(canonicalValue), unitAffineMap(unit));
}

/**
 * Migrate the **legacy** ADR-0002 number value — `{ value, unit? }`, where
 * `value` was stored in the *display* unit as a JS number — to the ADR-0007
 * dimensioned form `{ canonical_value, unit_id }` (or a bare number when
 * unitless). The `unit` may be a new opaque `unit_…` id or a former readable
 * token (`metric.km`); both resolve through `registry` + the frozen
 * common-unit map. Falls back to the bare number if the unit can't be
 * resolved (best effort — the provenance unit is then lost, but the
 * magnitude survives). There are no production users (ADR-0007: clean
 * adoption), so this exists for completeness and any seeded/test data.
 */
export function migrateLegacyQuantity(
  legacy: { value: number; unit?: string },
  registry: UnitRegistry,
): number | DimensionedValue {
  if (legacy.unit === undefined) return legacy.value;
  const resolvedId =
    registry.unit(legacy.unit) !== null
      ? legacy.unit
      : (COMMON_UNIT_IDS as Record<string, string>)[legacy.unit];
  const unit = resolvedId !== undefined ? registry.unit(resolvedId) : null;
  if (unit === null) return legacy.value;
  return { canonical_value: toCanonicalValue(legacy.value, unit), unit_id: unit.id };
}

/**
 * A registry of dimensions + units, keyed by id, with a dimension index for
 * the unit picker. Campaigns populate this from subscribed unit packs plus
 * campaign-local definitions; the common sets below seed the defaults.
 */
export class UnitRegistry {
  private readonly dimensions = new Map<string, DimensionDefinition>();
  private readonly units = new Map<string, UnitDefinition>();

  addDimension(dimension: DimensionDefinition): void {
    this.dimensions.set(dimension.id, dimension);
  }

  addUnit(unit: UnitDefinition): void {
    this.units.set(unit.id, unit);
  }

  dimension(id: string): DimensionDefinition | null {
    return this.dimensions.get(id) ?? null;
  }

  unit(id: string): UnitDefinition | null {
    return this.units.get(id) ?? null;
  }

  /** Units belonging to a dimension (for the picker). */
  unitsForDimension(dimensionId: string): UnitDefinition[] {
    return [...this.units.values()].filter((u) => u.dimension === dimensionId);
  }

  listDimensions(): DimensionDefinition[] {
    return [...this.dimensions.values()];
  }

  listUnits(): UnitDefinition[] {
    return [...this.units.values()];
  }

  /** Convert between two registered unit ids; null if unknown or cross-dimension. */
  convert(value: Decimal, fromId: string, toId: string): Decimal | null {
    const from = this.unit(fromId);
    const to = this.unit(toId);
    if (!from || !to) return null;
    return convert(value, from, to);
  }
}

// ─── Display preferences (08-units.md §5, #249) ────────────────────────
//
// Which unit a dimension renders in (per campaign, per user) is a SETTING,
// not a definition (ADR-0007 §5 / ADR-0004 three-bucket): a per-dimension
// pointer holding a `unit_…` id, exactly like `display_calendars` points at
// calendar definitions. The stored `canonical_value` is NEVER touched — only
// the rendered unit converts (a fact authored in km renders as `155.34 mi`
// for a user whose length preference resolves to miles). Export carries the
// canonical value, never the display projection.

/**
 * A per-dimension unit-display map: `{ [dimension_id]: unit_id }`. Used for
 * both the campaign default (a `config.set` value — see `config-ops.ts`) and
 * a user's User-scope override. An absent dimension means "no preference"
 * (fall through to the next resolution tier).
 */
export type UnitDisplayMap = Record<string, string>;

/**
 * Resolve the unit a dimensioned value should RENDER in, per the §5 order:
 *   1. the user's per-dimension override,
 *   2. the campaign's per-dimension default,
 *   3. the value's own provenance `unit_id` (no conversion).
 *
 * A preference is honoured only when it names a unit that (a) is registered
 * and (b) shares the provenance unit's dimension — a stale/cross-dimension
 * pointer is ignored (defence-in-depth; the picker only offers same-dimension
 * units, but a pack retire or an alias edit could leave a dangling pointer).
 * Returns the resolved {@link UnitDefinition} to render in, or `null` when the
 * provenance unit itself is unknown to `registry` (caller can't render at all).
 *
 * Pure — no I/O, no canonical-value mutation. The single resolution chokepoint
 * for canonical→display unit selection, shared by every render surface.
 */
export function resolveDisplayUnit(
  provenanceUnitId: string,
  registry: UnitRegistry,
  prefs: { user?: UnitDisplayMap; campaign?: UnitDisplayMap } = {},
): UnitDefinition | null {
  const provenance = registry.unit(provenanceUnitId);
  if (provenance === null) return null;

  for (const map of [prefs.user, prefs.campaign]) {
    const preferredId = map?.[provenance.dimension];
    if (preferredId === undefined) continue;
    const preferred = registry.unit(preferredId);
    // Ignore a pointer to an unknown or cross-dimension unit; fall through.
    if (preferred !== null && preferred.dimension === provenance.dimension) {
      return preferred;
    }
  }
  return provenance;
}

const loc = (en: string): LocalizedString => ({ en });
const D = COMMON_DIMENSION_IDS;
const U = COMMON_UNIT_IDS;

/** Dimensions shipped with the common unit sets (ADR-0007 §5). */
export const COMMON_DIMENSIONS: readonly DimensionDefinition[] = [
  { id: D.length, name: loc('Length'), base_unit: U['metric.m'] },
  { id: D.mass, name: loc('Mass'), base_unit: U['metric.kg'] },
  { id: D.temperature, name: loc('Temperature'), base_unit: U['metric.celsius'] },
  { id: D.time, name: loc('Duration'), base_unit: U['time.second'] },
  // Currency canonicalises on the minor unit (copper), so monetary canonical
  // values are exact integers (ADR-0007 §3/§4).
  { id: D.currency, name: loc('Currency'), base_unit: U['currency.cp'] },
];

/**
 * The common unit sets the engine ships (08-units.md §5): metric, imperial,
 * a temperature pair, durations, and a generic coin set. A campaign opts in
 * to the sets it wants; the picker groups by `system`. Factor strings are
 * exact (decimal, or a fraction for °F's non-terminating slope).
 */
export const COMMON_UNITS: readonly UnitDefinition[] = [
  // Length — metric (base: metre)
  { id: U['metric.mm'], name: loc('Millimetre'), symbol: loc('mm'), dimension: D.length, to_base: '0.001', system: 'metric' },
  { id: U['metric.cm'], name: loc('Centimetre'), symbol: loc('cm'), dimension: D.length, to_base: '0.01', system: 'metric' },
  { id: U['metric.m'], name: loc('Metre'), symbol: loc('m'), dimension: D.length, to_base: '1', system: 'metric' },
  { id: U['metric.km'], name: loc('Kilometre'), symbol: loc('km'), dimension: D.length, to_base: '1000', system: 'metric' },
  // Length — imperial
  { id: U['imperial.in'], name: loc('Inch'), symbol: loc('in'), dimension: D.length, to_base: '0.0254', system: 'imperial' },
  { id: U['imperial.ft'], name: loc('Foot'), symbol: loc('ft'), dimension: D.length, to_base: '0.3048', system: 'imperial' },
  { id: U['imperial.yd'], name: loc('Yard'), symbol: loc('yd'), dimension: D.length, to_base: '0.9144', system: 'imperial' },
  { id: U['imperial.mi'], name: loc('Mile'), symbol: loc('mi'), dimension: D.length, to_base: '1609.344', system: 'imperial' },
  // Mass — metric (base: kilogram)
  { id: U['metric.g'], name: loc('Gram'), symbol: loc('g'), dimension: D.mass, to_base: '0.001', system: 'metric' },
  { id: U['metric.kg'], name: loc('Kilogram'), symbol: loc('kg'), dimension: D.mass, to_base: '1', system: 'metric' },
  { id: U['metric.t'], name: loc('Tonne'), symbol: loc('t'), dimension: D.mass, to_base: '1000', system: 'metric' },
  // Mass — imperial
  { id: U['imperial.oz'], name: loc('Ounce'), symbol: loc('oz'), dimension: D.mass, to_base: '0.0283495', system: 'imperial' },
  { id: U['imperial.lb'], name: loc('Pound'), symbol: loc('lb'), dimension: D.mass, to_base: '0.453592', system: 'imperial' },
  // Temperature — affine (base: Celsius). °F slope 5/9 is exact as a fraction.
  { id: U['metric.celsius'], name: loc('Celsius'), symbol: loc('°C'), dimension: D.temperature, to_base: '1', offset: '0', system: 'metric' },
  { id: U['imperial.fahrenheit'], name: loc('Fahrenheit'), symbol: loc('°F'), dimension: D.temperature, to_base: '5/9', offset: '-160/9', system: 'imperial' },
  // Time / duration (base: second)
  { id: U['time.second'], name: loc('Second'), symbol: loc('s'), dimension: D.time, to_base: '1', system: 'metric' },
  { id: U['time.minute'], name: loc('Minute'), symbol: loc('min'), dimension: D.time, to_base: '60', system: 'metric' },
  { id: U['time.hour'], name: loc('Hour'), symbol: loc('h'), dimension: D.time, to_base: '3600', system: 'metric' },
  { id: U['time.day'], name: loc('Day'), symbol: loc('d'), dimension: D.time, to_base: '86400', system: 'metric' },
  // Currency (base: copper piece). Integer ratios ⇒ exact minor-unit canonical
  // values. Cross-currency exchange goes through exchange-rate facts (§7), not
  // these constants.
  { id: U['currency.cp'], name: loc('Copper piece'), symbol: loc('cp'), dimension: D.currency, to_base: '1', system: 'currency' },
  { id: U['currency.sp'], name: loc('Silver piece'), symbol: loc('sp'), dimension: D.currency, to_base: '10', system: 'currency' },
  { id: U['currency.ep'], name: loc('Electrum piece'), symbol: loc('ep'), dimension: D.currency, to_base: '50', system: 'currency' },
  { id: U['currency.gp'], name: loc('Gold piece'), symbol: loc('gp'), dimension: D.currency, to_base: '100', system: 'currency' },
  { id: U['currency.pp'], name: loc('Platinum piece'), symbol: loc('pp'), dimension: D.currency, to_base: '1000', system: 'currency' },
];

/** Build a registry pre-seeded with the common dimensions + units. */
export function commonUnitRegistry(): UnitRegistry {
  const reg = new UnitRegistry();
  for (const d of COMMON_DIMENSIONS) reg.addDimension(d);
  for (const u of COMMON_UNITS) reg.addUnit(u);
  return reg;
}

/**
 * Build a registry that layers a campaign-local overlay of dimensions +
 * units on top of {@link commonUnitRegistry} (ADR-0007 §5 / ADR-0001 pack
 * fold).
 *
 * The overlay shape mirrors the campaign homebrew Pack fold: the
 * `dimensions` / `units` maps are keyed by id, and `retiredDimensions` /
 * `retiredUnits` are tombstone sets. Overlay definitions win on id collision
 * with the common set; retired ids are dropped from the result. The returned
 * `UnitRegistry` exposes the same API (and `unitsForDimension` picker index)
 * as the common one, so callers can swap them transparently.
 *
 * Pure — builds a fresh `UnitRegistry` and never mutates the common
 * singletons.
 */
export function mergeUnitRegistry(overlay: {
  dimensions: Record<string, DimensionDefinition>;
  units: Record<string, UnitDefinition>;
  retiredDimensions?: Record<string, true>;
  retiredUnits?: Record<string, true>;
}): UnitRegistry {
  const retiredDimensions = overlay.retiredDimensions ?? {};
  const retiredUnits = overlay.retiredUnits ?? {};

  // Merge dimensions: common first, overlay wins on collision.
  const dimensions = new Map<string, DimensionDefinition>();
  for (const d of COMMON_DIMENSIONS) dimensions.set(d.id, d);
  for (const id of Object.keys(overlay.dimensions)) {
    const d = overlay.dimensions[id];
    if (d !== undefined) dimensions.set(id, d);
  }

  // Merge units: common first, overlay wins on collision.
  const units = new Map<string, UnitDefinition>();
  for (const u of COMMON_UNITS) units.set(u.id, u);
  for (const id of Object.keys(overlay.units)) {
    const u = overlay.units[id];
    if (u !== undefined) units.set(id, u);
  }

  // Drop retired ids, then rebuild the registry (which re-indexes
  // `unitsForDimension` lazily on read).
  const reg = new UnitRegistry();
  for (const [id, d] of dimensions) {
    if (!retiredDimensions[id]) reg.addDimension(d);
  }
  for (const [id, u] of units) {
    if (!retiredUnits[id]) reg.addUnit(u);
  }
  return reg;
}
